import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import DocumentUploader from 'components/documents/DocumentUploader';
import DocumentList from 'components/documents/DocumentList';
import { useTranslation } from 'i18n/TranslationProvider';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};
const normalizeCapacity = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 20) {
    return null;
  }
  return parsed;
};
const normalizeMaxLoadKg = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 100000) {
    return null;
  }
  return Math.round(parsed);
};

const AddEditTruckModal = ({ isOpen, onClose, onSave, truck }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [formData, setFormData] = useState({});
  const [refreshDocs, setRefreshDocs] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUploadedCmrThisSession, setHasUploadedCmrThisSession] = useState(false);
  const [selectedFilesCount, setSelectedFilesCount] = useState(0);
  const uploaderRef = useRef(null);

  const panelBg = useColorModeValue('rgba(255,255,255,0.96)', 'rgba(13,23,53,0.88)');
  const panelBorder = useColorModeValue('rgba(226,232,240,0.9)', 'rgba(255,255,255,0.16)');
  const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (!isOpen) return;

    if (truck) {
      setFormData(truck);
      setHasUploadedCmrThisSession(true);
    } else {
      setFormData({ licensePlate: '', carCapacity: '', maxLoadKg: '' });
      setHasUploadedCmrThisSession(false);
    }
    setRefreshDocs(0);
    setSelectedFilesCount(0);
  }, [isOpen, truck]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isNewTruckFlow = !truck;
  const hasPersistedTruck = Boolean(formData?._id);
  const closeBlocked = isNewTruckFlow && hasPersistedTruck && !hasUploadedCmrThisSession;
  const expiryLabel = formatDate(formData?.cmrInsuranceExpiryDate);
  const shouldUploadNewCmr = selectedFilesCount > 0;

  const handleSave = async () => {
    const trimmedPlate = String(formData.licensePlate || '').trim().toUpperCase();
    const carCapacity = normalizeCapacity(formData.carCapacity);
    const maxLoadKg = normalizeMaxLoadKg(formData.maxLoadKg);
    const hasMaxLoadInput = String(formData.maxLoadKg ?? '').trim() !== '';
    if (!trimmedPlate) {
      toast({
        title: t('License plate is required.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!carCapacity) {
      toast({
        title: t('Car capacity is required.'),
        description: t('Enter a valid capacity between 1 and 20 cars.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (hasMaxLoadInput && !maxLoadKg) {
      toast({
        title: t('Invalid max load (kg).'),
        description: t('Enter a valid weight between 1 and 100000 kg, or leave it empty.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (isNewTruckFlow) {
      if (selectedFilesCount === 0) {
        toast({
          title: t('CMR Insurance required'),
          description: t('Attach the CMR Insurance before finishing truck setup.'),
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    }

    if (shouldUploadNewCmr && !uploaderRef.current?.upload) {
      toast({
        title: t('Upload unavailable'),
        description: t('Please try again.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      if (!isNewTruckFlow && formData?._id) {
        const updatedTruck = await onSave({
          _id: formData._id,
          licensePlate: trimmedPlate,
          carCapacity,
          maxLoadKg: maxLoadKg ?? null,
        });
        if (updatedTruck?._id) {
          setFormData(updatedTruck);
        }
      }

      if (shouldUploadNewCmr) {
        const uploadResult = await uploaderRef.current.upload();
        if (!uploadResult?.success) {
          return;
        }

        setHasUploadedCmrThisSession(true);
        setRefreshDocs((prev) => prev + 1);
      } else if (isNewTruckFlow && formData?._id) {
        return;
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const ensureTruckForUpload = async () => {
    const trimmedPlate = String(formData.licensePlate || '').trim().toUpperCase();
    const carCapacity = normalizeCapacity(formData.carCapacity);
    const maxLoadKg = normalizeMaxLoadKg(formData.maxLoadKg);
    const hasMaxLoadInput = String(formData.maxLoadKg ?? '').trim() !== '';
    if (!trimmedPlate) {
      toast({
        title: t('License plate is required.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      throw new Error('License plate is required');
    }
    if (!carCapacity) {
      toast({
        title: t('Car capacity is required.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      throw new Error('Car capacity is required');
    }
    if (hasMaxLoadInput && !maxLoadKg) {
      toast({
        title: t('Invalid max load (kg).'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      throw new Error('Invalid max load (kg)');
    }
    if (formData?._id) {
      return formData._id;
    }

    const savedTruck = await onSave({
      licensePlate: trimmedPlate,
      carCapacity,
      maxLoadKg: maxLoadKg ?? null,
    });
    if (!savedTruck?._id) {
      throw new Error('Unable to create truck record');
    }
    setFormData(savedTruck);
    return savedTruck._id;
  };

  const handleCloseRequest = () => {
    if (closeBlocked) {
      toast({
        title: t('CMR Insurance required'),
        description: t('Upload a CMR Insurance before closing this truck setup.'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseRequest}
      size="xl"
      closeOnEsc={!closeBlocked}
      closeOnOverlayClick={!closeBlocked}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        bg={panelBg}
        border="1px solid"
        borderColor={panelBorder}
        borderRadius="22px"
        boxShadow="0 22px 54px rgba(9, 30, 66, 0.24)"
      >
        <ModalHeader>
          {truck ? t('Edit Truck') : t('Add Truck')}
        </ModalHeader>
        <ModalCloseButton isDisabled={closeBlocked} />

        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t('License Plate')}</FormLabel>
              <Input
                name="licensePlate"
                value={formData.licensePlate || ''}
                onChange={handleChange}
                textTransform="uppercase"
                placeholder={t('Enter license plate')}
                bg={subtleBg}
                borderRadius="12px"
                isReadOnly={!isNewTruckFlow}
                isDisabled={!isNewTruckFlow || isSaving}
              />
              {!isNewTruckFlow && (
                <Text mt={2} fontSize="xs" color={mutedText}>
                  {t('License plate cannot be changed after creation. Re-upload CMR Insurance instead.')}
                </Text>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('Car Capacity')}</FormLabel>
              <Input
                type="number"
                name="carCapacity"
                value={formData.carCapacity ?? ''}
                onChange={handleChange}
                min={1}
                max={20}
                placeholder={t('How many cars can this truck carry?')}
                bg={subtleBg}
                borderRadius="12px"
                isDisabled={isSaving}
              />
              <Text mt={2} fontSize="xs" color={mutedText}>
                {t('Set maximum number of cars this truck can carry (1-20).')}
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>{t('Weight (kg)')}</FormLabel>
              <Input
                type="number"
                name="maxLoadKg"
                value={formData.maxLoadKg ?? ''}
                onChange={handleChange}
                min={1}
                max={100000}
                placeholder="10000"
                bg={subtleBg}
                borderRadius="12px"
                isDisabled={isSaving}
              />
              <Text mt={2} fontSize="xs" color={mutedText}>
                {t('Maximum payload weight this truck can carry (optional).')}
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>{t('CMR Insurance Expiry Date')}</FormLabel>
              <Input
                isReadOnly
                value={expiryLabel || t('Pending admin review')}
                bg={subtleBg}
                borderRadius="12px"
              />
              <Text mt={2} fontSize="xs" color={mutedText}>
                {t('Only admins can set or update this expiry date after reviewing your CMR insurance.')}
              </Text>
            </FormControl>

            <Box border="1px solid" borderColor={panelBorder} borderRadius="14px" p={4}>
              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="700">{t('CMR Insurance')}</Text>
                  <Text fontSize="sm" color={mutedText}>
                    {t('Upload a valid CMR insurance. Each upload sends the truck back to pending review.')}
                  </Text>
                </Box>

                {!hasPersistedTruck && (
                  <Badge alignSelf="flex-start" colorScheme="blue" borderRadius="full" px={3} py={1}>
                    {t('Enter plate and upload CMR to add truck')}
                  </Badge>
                )}

                <DocumentUploader
                  ref={uploaderRef}
                  entityType="truck"
                  entityId={formData._id}
                  ensureEntityId={isNewTruckFlow ? ensureTruckForUpload : undefined}
                  docType="cmr"
                  isDisabled={isSaving}
                  hideUploadButton
                  onFilesChange={(nextFiles) => setSelectedFilesCount(nextFiles.length)}
                  onUploaded={() => {
                    setHasUploadedCmrThisSession(true);
                    setRefreshDocs((prev) => prev + 1);
                  }}
                />
                {hasPersistedTruck && (
                  <DocumentList
                    entityType="truck"
                    entityId={formData._id}
                    docType="cmr"
                    refreshTrigger={refreshDocs}
                    canDelete={false}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter gap={2} flexWrap="wrap">
          <Button
            variant="ghost"
            onClick={handleCloseRequest}
            isDisabled={closeBlocked}
          >
            {t('Close')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
          >
            {isNewTruckFlow ? t('Done') : t('Re-upload CMR')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddEditTruckModal;
